import React, { useCallback, useEffect, useState } from 'react';
import {
  AlertCircle, Check, EyeOff, Loader2, Pencil, Plus, Trash2, X,
} from 'lucide-react';
import { adminApi } from '../services/api.jsx';
import {
  annoncerErreur, annoncerSucces, Bandeau, EtatErreur, EtatSansDonnees, SqueletteListe,
} from '../etats';

/* ═══════════════════════════════════════════════════════════════════════════
   FORMULES TARIFAIRES

   Les cinq formules étaient écrites en dur dans le serveur. Corriger un prix
   demandait un développeur, un commit et un déploiement — pour une décision
   commerciale qui se prend en une minute.

   Deux gestes bien distincts, et l'interface refuse de les confondre :

     - DÉSACTIVER retire la formule de la page publique. Les demandes et les
       projets qui la citent gardent son nom et son prix.
     - SUPPRIMER efface la ligne. Le serveur le refuse tant qu'un dossier s'y
       réfère, et dit combien : sans ce garde-fou, un clic viderait le libellé
       et le montant de contrats déjà signés.

   Le code d'une formule ne se modifie pas après sa création. Il est recopié
   dans chaque demande enregistrée ; le changer les orphelinerait toutes.
   ═══════════════════════════════════════════════════════════════════════════ */

const VIDE = {
  code: '', nom: '', categorie: 'formation', prix_fcfa: '', duree: '',
  duree_mois: '', resume: '', avantages: '', quotas: [], inclut: [], echelonnable: false, actif: true,
};

/* Les avantages ÉLÉMENTAIRES : la liste vient du serveur, chacun avec sa
   règle (une séance de trop est refusée, le certificat n'est pas émis hors
   formule…). On coche ce que la formule comprend ; un avantage « nombre »
   se chiffre, un avantage « inclus » se coche seulement. Les précisions
   (séances, participants) sont facultatives : absentes, l'avantage parent
   compris suffit, sans plafond. */
const FAMILLES = [['formation', 'Formation'], ['certification', 'Certification'], ['accompagnement', 'Accompagnement']];

function AvantagesChiffres({ possibles, valeur, onChange }) {
  const trouve = (code) => valeur.find((v) => v.code === code);
  const basculer = (p) => (trouve(p.code)
    ? onChange(valeur.filter((v) => v.code !== p.code))
    : onChange([...valeur, { code: p.code, quantite: 1 }]));
  const chiffrer = (code, quantite) => onChange(valeur.map((v) => (v.code === code ? { ...v, quantite } : v)));

  return (
    <div className="grid gap-3 sm:grid-cols-3">
      {FAMILLES.map(([famille, titre]) => {
        const dela = possibles.filter((p) => p.famille === famille);
        if (!dela.length) return null;
        return (
          <fieldset key={famille} className="rounded-xl border border-[var(--border)] bg-[var(--bg-card)] p-3">
            <legend className="px-1 text-[11.5px] font-semibold uppercase tracking-wide text-[var(--text-muted)]">{titre}</legend>
            <ul className="space-y-2">
              {dela.map((p) => {
                const v = trouve(p.code);
                return (
                  <li key={p.code} className={p.precise ? 'pl-5' : ''}>
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input type="checkbox" checked={!!v} onChange={() => basculer(p)}
                             className="w-4 h-4 mt-0.5 shrink-0" style={{ accentColor: 'var(--accent)' }} />
                      <span className="min-w-0">
                        <span className="block text-[13px] font-semibold text-[var(--text-bold)]">
                          {p.libelle}{p.precise ? <span className="font-normal text-[var(--text-muted)]"> (facultatif)</span> : null}
                        </span>
                        <span className="block text-[11.5px] text-[var(--text-muted)]">
                          {p.regle === 'suivi' ? "Porté au contrat et affiché ; pas encore distribué automatiquement." : p.regle}
                        </span>
                      </span>
                    </label>
                    {v && p.type === 'nombre' && (
                      <input type="number" min="1" step="1" value={v.quantite}
                             aria-label={`Nombre : ${p.libelle}`}
                             onChange={(e) => chiffrer(p.code, e.target.value)}
                             style={{ ...champ, width: 90, minHeight: 34, marginTop: 4, marginLeft: 24 }} />
                    )}
                  </li>
                );
              })}
            </ul>
          </fieldset>
        );
      })}
    </div>
  );
}

/* Une formule peut reprendre TOUS les avantages d'autres formules :
   « Accompagnement » inclut la formation complète, « Complet » inclut la
   formation complète, la labellisation et l'accompagnement. Le serveur
   calcule la somme (chaque formule une fois, la valeur la plus haute) et
   refuse une boucle. */
function FormulesIncluses({ autres, valeur, onChange }) {
  if (!autres.length) return <p className="text-[12.5px] text-[var(--text-muted)]">Aucune autre formule au catalogue.</p>;
  return (
    <div className="flex flex-wrap gap-2">
      {autres.map((f) => {
        const coche = valeur.includes(f.code);
        return (
          <label key={f.code}
                 className={`flex items-center gap-2 rounded-controle border px-3 py-2 text-[13px] cursor-pointer ${coche
                   ? 'border-[var(--accent)] bg-[var(--accent)]/10 text-[var(--text-bold)] font-semibold'
                   : 'border-[var(--border)] bg-[var(--bg-card)] text-[var(--text-main)]'}`}>
            <input type="checkbox" checked={coche} className="w-4 h-4" style={{ accentColor: 'var(--accent)' }}
                   onChange={() => onChange(coche ? valeur.filter((c) => c !== f.code) : [...valeur, f.code])} />
            {f.nom}
          </label>
        );
      })}
    </div>
  );
}

const formaterPrix = (montant) =>
  `${new Intl.NumberFormat('fr-FR').format(montant || 0).replace(/ | |\s/g, ' ')} FCFA`;

const champ = {
  width: '100%', minHeight: 42, padding: '9px 12px', borderRadius: 8,
  border: '1px solid var(--border)', background: 'var(--bg-main)',
  color: 'var(--text-bold)', fontSize: 14, outline: 'none',
};

function Ligne({ label, aide, children, large }) {
  return (
    <div className={large ? 'sm:col-span-2' : ''}>
      <label className="block text-[12.5px] font-semibold text-[var(--text-main)] mb-1.5">
        {label}
      </label>
      {children}
      {aide && (
        <p className="text-[11.5px] text-[var(--text-muted)] mt-1 leading-relaxed">{aide}</p>
      )}
    </div>
  );
}

function Formulaire({ formule, categories, avantagesPossibles = [], formules = [], onAnnuler, onEnregistre }) {
  const creation = !formule;
  const [form, setForm] = useState(() => (
    formule
      ? {
          code: formule.code,
          nom: formule.nom || '',
          categorie: formule.categorie || 'formation',
          prix_fcfa: String(formule.prix_fcfa ?? ''),
          duree: formule.duree || '',
          duree_mois: formule.duree_mois == null ? '' : String(formule.duree_mois),
          resume: formule.resume || '',
          avantages: (formule.avantages || []).join('\n'),
          quotas: Object.entries(formule.quotas || {}).map(([code, quantite]) => ({ code, quantite })),
          inclut: [...(formule.inclut || [])],
          echelonnable: !!formule.echelonnable,
          actif: formule.actif !== false,
        }
      : { ...VIDE }
  ));
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState('');

  const maj = (cle) => (e) => setForm((f) => ({
    ...f,
    [cle]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
  }));

  const enregistrer = async (e) => {
    e.preventDefault();
    setErreur('');
    setEnCours(true);
    const corps = {
      nom: form.nom.trim(),
      categorie: form.categorie,
      prix_fcfa: form.prix_fcfa,
      duree: form.duree.trim(),
      duree_mois: form.duree_mois === '' ? null : form.duree_mois,
      resume: form.resume.trim(),
      avantages: form.avantages,
      quotas: form.quotas.map((q) => ({ code: q.code, quantite: Number(q.quantite) })),
      inclut: form.inclut,
      echelonnable: form.echelonnable,
      actif: form.actif,
    };
    try {
      if (creation) await adminApi.creerFormule({ ...corps, code: form.code.trim() });
      else await adminApi.majFormule(form.code, corps);
      onEnregistre();
    } catch (err) {
      setErreur(err.message || "Enregistrement impossible.");
    } finally {
      setEnCours(false);
    }
  };

  return (
    <form onSubmit={enregistrer}
          className="rounded-pastille border border-[var(--accent)]/40 bg-[var(--bg-main)] p-5 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h3 className="text-[14px] font-semibold text-[var(--text-bold)]">
          {creation ? 'Nouvelle formule' : `Modifier « ${formule.nom} »`}
        </h3>
        <button type="button" onClick={onAnnuler} aria-label="Fermer"
                className="act act-icone act-tertiaire">
          <X className="w-4 h-4" aria-hidden="true" />
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {creation && (
          <Ligne label="Code" aide="Sans espace. Il ne pourra plus être modifié.">
            <input type="text" required value={form.code} onChange={maj('code')}
                   placeholder="accompagnement_premium" style={champ} />
          </Ligne>
        )}

        <Ligne label="Nom" large={!creation}>
          <input type="text" required maxLength={160} value={form.nom}
                 onChange={maj('nom')} style={champ} />
        </Ligne>

        <Ligne label="Catégorie" aide="Elle détermine le circuit de traitement de la demande.">
          <select value={form.categorie} onChange={maj('categorie')} style={champ}>
            {categories.map((c) => (
              <option key={c.code} value={c.code}>{c.libelle}</option>
            ))}
          </select>
        </Ligne>

        <Ligne label="Prix (FCFA)" aide="Entier, sans décimale ni séparateur.">
          <input type="number" min="0" step="1" required value={form.prix_fcfa}
                 onChange={maj('prix_fcfa')} style={champ} />
        </Ligne>

        <Ligne label="Durée affichée" aide="Telle qu'on l'annonce : « 6 semaines », « 6 mois ».">
          <input type="text" required maxLength={60} value={form.duree}
                 onChange={maj('duree')} style={champ} />
        </Ligne>

        <Ligne label="Durée en mois"
               aide="Obligatoire si la formule est échelonnable : c'est elle qui découpe les mensualités.">
          <input type="number" min="1" step="1" value={form.duree_mois}
                 onChange={maj('duree_mois')} style={champ} />
        </Ligne>

        <Ligne label="Résumé" large aide="Une phrase, affichée sous le nom sur la page des tarifs.">
          <input type="text" maxLength={255} value={form.resume}
                 onChange={maj('resume')} style={champ} />
        </Ligne>

        <Ligne label="Reprend tous les avantages de" large
               aide="Les avantages des formules cochées s'ajoutent à ceux de cette formule, chacun une seule fois.">
          <FormulesIncluses autres={formules.filter((f) => f.code !== form.code)} valeur={form.inclut}
                            onChange={(inclut) => setForm((f) => ({ ...f, inclut }))} />
        </Ligne>

        <Ligne label="Avantages propres à cette formule" large
               aide="Ce que la formule ajoute — ou corrige — par rapport aux formules reprises. C'est la plateforme qui les fait respecter.">
          <AvantagesChiffres possibles={avantagesPossibles} valeur={form.quotas}
                             onChange={(quotas) => setForm((f) => ({ ...f, quotas }))} />
        </Ligne>

        <Ligne label="Autres mentions" large
               aide="Affichées sur la page des tarifs, sans règle associée (« rapport final », « attestation »). Une par ligne.">
          <textarea rows={3} value={form.avantages} onChange={maj('avantages')}
                    style={{ ...champ, minHeight: 80, resize: 'vertical' }} />
        </Ligne>
      </div>

      <div className="flex flex-wrap gap-5 pt-1">
        <label className="flex items-center gap-2 text-[13px] text-[var(--text-main)] cursor-pointer">
          <input type="checkbox" checked={form.echelonnable} onChange={maj('echelonnable')}
                 className="w-4 h-4" style={{ accentColor: 'var(--accent)' }} />
          Paiement échelonné autorisé
        </label>
        <label className="flex items-center gap-2 text-[13px] text-[var(--text-main)] cursor-pointer">
          <input type="checkbox" checked={form.actif} onChange={maj('actif')}
                 className="w-4 h-4" style={{ accentColor: 'var(--accent)' }} />
          Visible sur la page des tarifs
        </label>
      </div>

      {erreur && (
        <p role="alert" className="flex items-start gap-2 text-[13px] text-[var(--alerte)]">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" aria-hidden="true" />
          <span className="leading-relaxed">{erreur}</span>
        </p>
      )}

      <div className="flex gap-3">
        <button type="submit" disabled={enCours} aria-busy={enCours || undefined}
                style={{ background: 'var(--accent)', color: '#0A1128' }}
                className="act act-tertiaire">
          {enCours
            ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
            : <Check className="w-4 h-4" aria-hidden="true" />}
          Enregistrer
        </button>
        <button type="button" onClick={onAnnuler}
                
                className="act act-contour">
          Annuler
        </button>
      </div>
    </form>
  );
}

export default function FormulesTarifaires({ peutModifier }) {
  const [formules, setFormules] = useState([]);
  const [categories, setCategories] = useState([]);
  const [avantagesPossibles, setAvantagesPossibles] = useState([]);
  // `charge` : au moins une réponse reçue. Seul le premier chargement
  // montre un squelette ; recharger après une action garde la liste.
  const [charge, setCharge] = useState(false);
  const [chargement, setChargement] = useState(true);
  // L'objet erreur du chargement : son statut choisit l'écran.
  const [erreur, setErreur] = useState(null);
  const [edition, setEdition] = useState(null);   // 'nouvelle' | code | null
  const [suppression, setSuppression] = useState(null);
  const [enVol, setEnVol] = useState('');

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const d = await adminApi.formulesAdmin();
      setFormules(Array.isArray(d?.formules) ? d.formules : []);
      setCategories(Array.isArray(d?.categories) ? d.categories : []);
      setAvantagesPossibles(Array.isArray(d?.avantages_possibles) ? d.avantages_possibles : []);
      setCharge(true);
      setErreur(null);
    } catch (e) {
      setErreur(e);
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const apresEnregistrement = (texte) => {
    setEdition(null);
    annoncerSucces(texte);
    charger();
  };

  const basculerVisibilite = async (f) => {
    setEnVol(f.code);
    try {
      await adminApi.majFormule(f.code, { actif: !f.actif });
      annoncerSucces(f.actif
        ? `« ${f.nom} » est retirée de la page des tarifs.`
        : `« ${f.nom} » est de nouveau sur la page des tarifs.`);
      charger();
    } catch (e) {
      annoncerErreur(e.message || "Ce changement n'a pas pu être enregistré.");
    } finally {
      setEnVol('');
    }
  };

  const supprimer = async (f) => {
    setEnVol(f.code);
    try {
      await adminApi.supprimerFormule(f.code);
      setSuppression(null);
      apresEnregistrement(`« ${f.nom} » a été supprimée.`);
    } catch (e) {
      annoncerErreur(e.message || 'Suppression impossible.');
      setSuppression(null);
    } finally {
      setEnVol('');
    }
  };

  return (
    <div className="space-y-4">
      {charge && erreur && (
        <Bandeau ton="attention" action={<button type="button" className="act act-sm act-contour" onClick={charger}>Réessayer</button>}>
          L’actualisation a échoué : ce catalogue est peut-être ancien.
        </Bandeau>
      )}

      {peutModifier && charge && edition !== 'nouvelle' && (
        <button type="button" onClick={() => setEdition('nouvelle')}
                
                className="act act-contour">
          <Plus className="w-4 h-4" aria-hidden="true" /> Ajouter une formule
        </button>
      )}

      {edition === 'nouvelle' && (
        <Formulaire
          formule={null}
          categories={categories}
          avantagesPossibles={avantagesPossibles}
          formules={formules}
          onAnnuler={() => setEdition(null)}
          onEnregistre={() => apresEnregistrement('Formule ajoutée au catalogue.')}
        />
      )}

      {!charge ? (
        erreur && !chargement
          ? <EtatErreur erreur={erreur} onReessayer={charger} />
          : <SqueletteListe lignes={4} />
      ) : formules.length === 0 ? (
        <EtatSansDonnees cas={peutModifier ? 'premiere' : 'vide'} compact
                         titre="Aucune formule au catalogue"
                         aide="La page des tarifs est vide tant qu'il en manque." />
      ) : (
        <div className="space-y-3">
          {formules.map((f) => (
            edition === f.code ? (
              <Formulaire
                key={f.code}
                formule={f}
                categories={categories}
                avantagesPossibles={avantagesPossibles}
                formules={formules}
                onAnnuler={() => setEdition(null)}
                onEnregistre={() => apresEnregistrement(`« ${f.nom} » a été mise à jour.`)}
              />
            ) : (
              <div key={f.code}
                   className="rounded-pastille border border-[var(--border)] p-4"
                   style={{ opacity: f.actif ? 1 : 0.62 }}>
                <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-[14px] font-semibold text-[var(--text-bold)]">{f.nom}</p>
                      <span className="px-2 py-0.5 rounded-controle text-[11px] font-semibold
                                       border border-[var(--border)] text-[var(--text-muted)]">
                        {categories.find((c) => c.code === f.categorie)?.libelle || f.categorie}
                      </span>
                      {!f.actif && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-controle text-[11px] font-semibold
                                         bg-[var(--attention)]/10 text-[var(--attention)]">
                          <EyeOff className="w-3 h-3" aria-hidden="true" /> Retirée du site
                        </span>
                      )}
                      {f.echelonnable && (
                        <span className="px-2 py-0.5 rounded-controle text-[11px] font-semibold
                                         bg-[var(--accent)]/12 text-[var(--accent-texte)] border border-[var(--accent)]/25">
                          Échelonnable
                        </span>
                      )}
                    </div>

                    <p className="text-[13px] text-[var(--text-muted)] mt-1">
                      <span className="font-semibold text-[var(--text-bold)]">{formaterPrix(f.prix_fcfa)}</span>
                      {' · '}{f.duree}
                    </p>
                    {f.inclut?.length > 0 && (
                      <p className="text-[12px] text-[var(--text-muted)] mt-2">
                        Reprend : {f.inclut.map((c) => formules.find((x) => x.code === c)?.nom || c).join(' · ')}
                      </p>
                    )}
                    {f.avantages_effectifs?.length > 0 ? (
                      <ul className="flex flex-wrap gap-1.5 mt-2">
                        {f.avantages_effectifs.map((a) => (
                          <li key={a.code} title={a.origine ? `Vient de « ${a.origine} »` : 'Propre à cette formule'}
                              className={`px-2 py-0.5 rounded-controle text-[11.5px] font-semibold border ${a.origine
                                ? 'bg-[var(--bg-main)] text-[var(--text-main)] border-[var(--border)]'
                                : 'bg-[var(--accent)]/12 text-[var(--accent-texte)] border-[var(--accent)]/25'}`}>
                            {a.phrase}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-[11.5px] text-[var(--attention)] mt-1">
                        Aucun avantage renseigné : aucune limite n'est appliquée aux projets.
                      </p>
                    )}

                    {(f.references?.demandes > 0 || f.references?.projets > 0) && (
                      <p className="text-[11.5px] text-[var(--text-muted)] mt-1">
                        Citée par {f.references.demandes} demande{f.references.demandes > 1 ? 's' : ''}
                        {' et '}{f.references.projets} projet{f.references.projets > 1 ? 's' : ''}.
                      </p>
                    )}
                  </div>

                  {peutModifier && (
                    <div className="flex items-center gap-2 shrink-0">
                      <button type="button" onClick={() => setEdition(f.code)}
                              title="Modifier cette formule"
                              style={{ minWidth: 36 }}
                              className="act act-contour">
                        <Pencil className="w-4 h-4" aria-hidden="true" />
                      </button>
                      <button type="button" onClick={() => basculerVisibilite(f)}
                              disabled={enVol === f.code} aria-busy={enVol === f.code || undefined}
                              title={f.actif
                                ? 'Retirer de la page des tarifs, sans toucher à l’historique'
                                : 'Remettre sur la page des tarifs'}
                              
                              className="act act-contour">
                        {enVol === f.code
                          ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                          : (f.actif ? 'Désactiver' : 'Réactiver')}
                      </button>
                      <button type="button" onClick={() => setSuppression(f)}
                              title="Supprimer définitivement"
                              style={{ minWidth: 36 }}
                              className="act act-danger">
                        <Trash2 className="w-4 h-4" aria-hidden="true" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )
          ))}
        </div>
      )}

      {/* Confirmation de suppression. Le serveur refusera de toute façon si un
          dossier s'y réfère ; cette fenêtre évite le clic malheureux sur les
          formules qui, elles, peuvent réellement disparaître. */}
      {suppression && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
             onClick={() => setSuppression(null)} role="presentation">
          <div onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true"
               className="w-full max-w-md rounded-carte shadow-carte bg-[var(--bg-card)] p-6 space-y-4">
            <h3 className="text-[16px] font-semibold text-[var(--text-bold)]">
              Supprimer « {suppression.nom} » ?
            </h3>
            <p className="text-[13px] text-[var(--text-main)] leading-relaxed">
              La formule disparaîtra du catalogue. Si vous voulez seulement la
              retirer de la page publique en gardant l'historique intact,
              utilisez <span className="font-semibold">Désactiver</span>.
            </p>
            <div className="flex gap-3">
              <button type="button" onClick={() => supprimer(suppression)}
                      disabled={enVol === suppression.code}
                      aria-busy={enVol === suppression.code || undefined}
                      
                      className="act act-tertiaire flex-1">
                {enVol === suppression.code
                  ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                  : <Trash2 className="w-4 h-4" aria-hidden="true" />}
                Supprimer
              </button>
              <button type="button" onClick={() => setSuppression(null)}
                      
                      className="act act-contour">
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
